import { useEffect, useState } from "react";
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card,
  FormControlLabel, Snackbar, Stack, Switch, TextField, Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { notifyConfigApi } from "../api/resources";
import { useLanguage } from "../i18n/LanguageContext";

const EMAIL_TYPES = [
  { key: "packageArrived", vars: ["name", "courier", "unit", "condo"] },
  { key: "deliveryConfirmed", vars: ["name", "date", "deliveredTo", "condo"] },
  { key: "bookingConfirmed", vars: ["name", "area", "date", "time", "condo"] },
];

export default function NotificacionesPage() {
  const { t } = useLanguage();
  const [form, setForm] = useState(null);
  const [toast, setToast] = useState(null);

  const load = () => notifyConfigApi.get().then(setForm);
  useEffect(() => {
    load();
  }, []);

  if (!form) return null;

  const setSmtp = (k) => (e) => setForm({ ...form, smtp: { ...form.smtp, [k]: e.target.value } });
  const setWhatsapp = (k) => (e) => setForm({ ...form, whatsapp: { ...form.whatsapp, [k]: e.target.value } });
  const setTemplateField = (type, field) => (e) =>
    setForm({
      ...form,
      emailTemplates: { ...form.emailTemplates, [type]: { ...form.emailTemplates[type], [field]: e.target.value } },
    });

  const save = async () => {
    const updated = await notifyConfigApi.update(form);
    setForm(updated);
    setToast(t("notifyConfig.saved"));
  };

  return (
    <Box>
      <Typography variant="h5">{t("notifyConfig.title")}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("notifyConfig.subtitle")}
      </Typography>

      <Card sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <AlternateEmailIcon fontSize="small" />
          <Typography fontWeight={600}>{t("notifyConfig.smtpSection")}</Typography>
        </Stack>
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("notifyConfig.smtpHint")}
        </Alert>
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField label={t("notifyConfig.smtpHost")} value={form.smtp.host} onChange={setSmtp("host")} fullWidth placeholder="smtp.tudominio.mx" />
            <TextField label={t("notifyConfig.smtpPort")} value={form.smtp.port ?? ""} onChange={setSmtp("port")} sx={{ width: { xs: "100%", sm: 140 } }} placeholder="587" />
          </Stack>
          <FormControlLabel
            control={<Switch checked={!!form.smtp.secure} onChange={(e) => setForm({ ...form, smtp: { ...form.smtp, secure: e.target.checked } })} />}
            label={t("notifyConfig.smtpSecure")}
          />
          <TextField label={t("notifyConfig.smtpUser")} value={form.smtp.user} onChange={setSmtp("user")} fullWidth placeholder="recepcion@tudominio.mx" />
          <TextField label={t("notifyConfig.smtpPass")} type="password" value={form.smtp.pass} onChange={setSmtp("pass")} fullWidth placeholder={t("notifyConfig.leaveBlankForEnv")} />
        </Stack>
      </Card>

      <Card sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <WhatsAppIcon fontSize="small" />
          <Typography fontWeight={600}>{t("notifyConfig.whatsappSection")}</Typography>
        </Stack>
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("notifyConfig.whatsappHint")}
        </Alert>
        <Stack spacing={2}>
          <TextField label={t("notifyConfig.whatsappToken")} type="password" value={form.whatsapp.token} onChange={setWhatsapp("token")} fullWidth placeholder={t("notifyConfig.leaveBlankForEnv")} />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField label={t("notifyConfig.whatsappPhoneId")} value={form.whatsapp.phoneNumberId} onChange={setWhatsapp("phoneNumberId")} fullWidth />
            <TextField label={t("notifyConfig.whatsappWabaId")} value={form.whatsapp.businessAccountId} onChange={setWhatsapp("businessAccountId")} fullWidth />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField label={t("notifyConfig.whatsappApiVersion")} value={form.whatsapp.apiVersion} onChange={setWhatsapp("apiVersion")} placeholder="v20.0" sx={{ flex: 1, minWidth: { xs: "100%", sm: 140 } }} />
            <TextField label={t("notifyConfig.whatsappTemplateLang")} value={form.whatsapp.templateLanguage} onChange={setWhatsapp("templateLanguage")} placeholder="es_MX" sx={{ flex: 1.4, minWidth: { xs: "100%", sm: 200 } }} />
            <TextField label={t("notifyConfig.whatsappWebhookToken")} type="password" value={form.whatsapp.webhookVerifyToken} onChange={setWhatsapp("webhookVerifyToken")} sx={{ flex: 1.4, minWidth: { xs: "100%", sm: 200 } }} />
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            {t("notifyConfig.templateNamesHint")}
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField label={t("notifyConfig.templatePackageArrived")} value={form.whatsapp.templatePackageArrived} onChange={setWhatsapp("templatePackageArrived")} fullWidth placeholder="paquete_recibido" />
            <TextField label={t("notifyConfig.templateDeliveryConfirmed")} value={form.whatsapp.templateDeliveryConfirmed} onChange={setWhatsapp("templateDeliveryConfirmed")} fullWidth placeholder="confirmacion_entrega" />
            <TextField label={t("notifyConfig.templateBookingConfirmed")} value={form.whatsapp.templateBookingConfirmed} onChange={setWhatsapp("templateBookingConfirmed")} fullWidth placeholder="reserva_confirmada" />
          </Stack>
        </Stack>
      </Card>

      <Card sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <EditNoteIcon fontSize="small" />
          <Typography fontWeight={600}>{t("notifyConfig.templatesSection")}</Typography>
        </Stack>
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("notifyConfig.templatesHint")}
        </Alert>

        {EMAIL_TYPES.map(({ key, vars }) => (
          <Accordion key={key} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" fontWeight={600}>
                {t(`notifyConfig.type_${key}`)}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                {t("notifyConfig.availableVars")}: {vars.map((v) => `{{${v}}}`).join(", ")}
              </Typography>
              <Stack spacing={2}>
                <Typography variant="caption" fontWeight={600}>{t("notifyConfig.spanish")}</Typography>
                <TextField label={t("notifyConfig.subject")} value={form.emailTemplates[key].subjectEs} onChange={setTemplateField(key, "subjectEs")} fullWidth placeholder={t("notifyConfig.leaveBlankForDefault")} />
                <TextField label={t("notifyConfig.body")} value={form.emailTemplates[key].bodyEs} onChange={setTemplateField(key, "bodyEs")} fullWidth multiline minRows={3} placeholder={t("notifyConfig.leaveBlankForDefault")} />
                <Typography variant="caption" fontWeight={600} sx={{ mt: 1 }}>{t("notifyConfig.english")}</Typography>
                <TextField label={t("notifyConfig.subject")} value={form.emailTemplates[key].subjectEn} onChange={setTemplateField(key, "subjectEn")} fullWidth placeholder={t("notifyConfig.leaveBlankForDefault")} />
                <TextField label={t("notifyConfig.body")} value={form.emailTemplates[key].bodyEn} onChange={setTemplateField(key, "bodyEn")} fullWidth multiline minRows={3} placeholder={t("notifyConfig.leaveBlankForDefault")} />
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Card>

      <Button variant="contained" startIcon={<CheckIcon />} onClick={save}>
        {t("notifyConfig.save")}
      </Button>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)}>
        <Alert severity="success" onClose={() => setToast(null)}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
}
